import { Box, Typography, Link } from "@mui/material";
import { Container } from "@mui/system";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";
import { useProductApi, Product, ProductMetadata } from "../hooks/ProductApiHook";
import { SnykIssue, useSnykApi } from "../hooks/SnykHook";

const SnykIssuesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const location = useLocation();

  const productApi = useProductApi();
  const [product, setProduct] = useState<Product>();

  const snykApi = useSnykApi();
  const [issues, setIssues] = useState<Array<SnykIssue>>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const GetProduct = async () => {
    if (id) {
      try {
        const product = await productApi.GetProduct(id);
        setProduct(product);
      } catch (error) {
        console.log(error);
      }
    }
  }

  useEffect(() => {
    GetProduct();
  }, [id]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        let data: Array<SnykIssue> = [];

        if (location.pathname.includes("code")) {
          data = await snykApi.GetCodeIssues(id);
        } else {
          data = await snykApi.GetSourceIssues(id);
        }

        setIssues(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);


  const rows = useMemo(
    () =>
      (issues || []).map((v) => ({
        id: v.id,
        severity: v.severity,
        status: v.issueStatus,
        issueURL: v.issueURL,
        firstIntroduced: v.firstIntroduced ? new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        }).format(new Date(v.firstIntroduced)) : "", title: v.problemTitle,
        organizationName: v.organizationName,
        directDependency: v.directDependency ? "Yes" : "No"
      })),
    [issues]
  );

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "title",
        headerName: "Title",
        flex: 2,
        minWidth: 110,
        renderCell: (params) => (
          <Link
            href={params.row.issueURL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontWeight: 500 }}
          >
            {params.value}
          </Link>
        ),
      },
      { field: "severity", headerName: "Severity", flex: 1, minWidth: 110 },
      { field: "status", headerName: "Status", flex: 1, minWidth: 110 },
      { field: "firstIntroduced", headerName: "Introduced", flex: 1, minWidth: 160 },
      { field: "organizationName", headerName: "Organization Name", flex: 1, minWidth: 250 },
    ];

    if (!location.pathname.includes("code")) {
      baseColumns.push({ field: "directDependency", headerName: "Direct Dependency", flex: 1, minWidth: 250 });
    }

    return baseColumns;
  }, [location.pathname]);

  return (
    <Container
      sx={{
        py: 2,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}>

      <Typography variant="h5" sx={{ mb: 2 }}>
        Snyk {location.pathname.includes("code") ? "Code" : "Open Source"} Issues - {product && product.name}
      </Typography>

      <Box sx={{ flex: 1 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Container>
  );
}

export default SnykIssuesPage;
